import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { axiosInstance } from "../App";
import { Plus, X, GitBranch, AlertCircle, CheckCircle2, Clock, Eye, List, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import DependencyGraph from "./DependencyGraph";

function TaskDependencies({ taskId, projectId, onUpdate }) {
  const { t } = useTranslation();
  const [dependencyChain, setDependencyChain] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [selectedDependency, setSelectedDependency] = useState("");
  const [dependencyType, setDependencyType] = useState("FS"); // FS, SS, FF, SF
  const [lagDays, setLagDays] = useState(0);
  const [lagHours, setLagHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [viewMode, setViewMode] = useState("graph"); // 'graph' or 'list'

  useEffect(() => {
    fetchDependencyChain();
    fetchProjectTasks();
  }, [taskId]);

  const fetchDependencyChain = async () => {
    try {
      const response = await axiosInstance.get(`/tasks/${taskId}/dependency-chain`);
      setDependencyChain(response.data);
    } catch (error) {
      console.error("Failed to fetch dependency chain", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectTasks = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/tasks`);
      // Filter out current task and its subtasks
      const filteredTasks = response.data.filter(t => t.id !== taskId && t.parent_task_id !== taskId);
      setProjectTasks(filteredTasks);
    } catch (error) {
      console.error("Failed to fetch project tasks", error);
    }
  };

  const handleAddDependency = async () => {
    if (!selectedDependency) {
      toast.error(t('pleaseSelectTask'));
      return;
    }

    setAdding(true);
    try {
      await axiosInstance.post(`/tasks/${taskId}/dependencies`, {
        dependency_task_id: selectedDependency,
        type: dependencyType,
        lag_days: parseInt(lagDays) || 0,
        lag_hours: parseInt(lagHours) || 0
      });
      toast.success(t('dependencyAdded'));
      setSelectedDependency("");
      setDependencyType("FS");
      setLagDays(0);
      setLagHours(0);
      fetchDependencyChain();
      if (onUpdate) onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('dependencyAddFailed');
      toast.error(errorMsg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveDependency = async (depId) => {
    try {
      await axiosInstance.delete(`/tasks/${taskId}/dependencies/${depId}`);
      toast.success(t('dependencyRemoved'));
      fetchDependencyChain();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(t('dependencyRemoveFailed'));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "done":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "in-progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    return t(`status.${status}`);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-100 text-red-800",
      medium: "bg-yellow-100 text-yellow-800",
      low: "bg-green-100 text-green-800"
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityLabel = (priority) => {
    return t(`priority.${priority}`);
  };

  // Calculate dependency statistics
  const getBlockedTasks = () => {
    return dependencyChain?.depends_on?.filter(dep => dep.status !== 'done') || [];
  };

  const getCompletionPercentage = () => {
    const total = dependencyChain?.depends_on?.length || 0;
    if (total === 0) return 100;
    const completed = dependencyChain?.depends_on?.filter(dep => dep.status === 'done').length || 0;
    return Math.round((completed / total) * 100);
  };

  // Filter out already added dependencies
  const availableTasks = projectTasks.filter(
    t => !dependencyChain?.depends_on?.some(dep => dep.id === t.id)
  );

  if (loading) {
    return <div className="text-sm text-gray-500">{t('loading')}</div>;
  }

  const blockedTasks = getBlockedTasks();
  const completionPercentage = getCompletionPercentage();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          <h4 className="font-semibold">{t('taskDependencies')}</h4>
        </div>
        {dependencyChain?.depends_on?.length > 0 && (
          <Badge variant="outline" className="text-xs">
            {completionPercentage}% {t('complete')}
          </Badge>
        )}
      </div>

      {/* Enhanced Blocked Warning with Details */}
      {dependencyChain?.is_blocked && blockedTasks.length > 0 && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="space-y-2">
            <p className="text-sm font-semibold text-orange-800">
              {t('taskBlocked')}
            </p>
            <p className="text-xs text-orange-700">
              {t('waitingForTasks', { count: blockedTasks.length })}:
            </p>
            <ul className="text-xs text-orange-700 list-disc list-inside space-y-1">
              {blockedTasks.map(task => (
                <li key={task.id}>
                  <span className="font-medium">{task.title}</span> - {getStatusLabel(task.status)}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Dependency Impact Alert */}
      {dependencyChain?.blocking && dependencyChain.blocking.length > 0 && (
        <Alert className="border-blue-200 bg-blue-50">
          <TrendingUp className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800">
            {t('taskImpact', { count: dependencyChain.blocking.length })}
          </AlertDescription>
        </Alert>
      )}

      {/* List View Only - Graph temporarily hidden */}
      {(dependencyChain?.depends_on?.length > 0 || dependencyChain?.blocking?.length > 0) && (
        <div className="w-full mt-4 space-y-4">
            {/* Depends On */}
            {dependencyChain?.depends_on && dependencyChain.depends_on.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">{t('thisTaskDependsOn')}:</p>
                <div className="space-y-2">
                  {dependencyChain.depends_on.map((dep) => (
                    <div
                      key={dep.id}
                      className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50"
                    >
                      {getStatusIcon(dep.status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{dep.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-gray-500">{getStatusLabel(dep.status)}</span>
                          <Badge className={`${getPriorityColor(dep.priority)} text-xs`} variant="secondary">
                            {getPriorityLabel(dep.priority)}
                          </Badge>
                          {dep.type && (
                            <Badge className="bg-purple-100 text-purple-800 text-xs" variant="secondary">
                              {dep.type}
                            </Badge>
                          )}
                          {(dep.lag_days !== 0 || dep.lag_hours !== 0) && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs" variant="secondary">
                              {dep.lag_days > 0 && `+${dep.lag_days}g `}
                              {dep.lag_days < 0 && `${dep.lag_days}g `}
                              {dep.lag_hours !== 0 && `${dep.lag_hours > 0 ? '+' : ''}${dep.lag_hours}s`}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleRemoveDependency(dep.id)}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Blocking */}
            {dependencyChain?.blocking && dependencyChain.blocking.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">{t('thisTaskBlocking')}:</p>
                <div className="space-y-2">
                  {dependencyChain.blocking.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-2 p-3 border rounded-lg bg-blue-50 border-blue-200"
                    >
                      {getStatusIcon(task.status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{getStatusLabel(task.status)}</span>
                          <Badge className={`${getPriorityColor(task.priority)} text-xs`} variant="secondary">
                            {getPriorityLabel(task.priority)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Add Dependency */}
      <div className="space-y-3 pt-4 border-t">
        <p className="text-sm font-medium text-gray-700">{t('addDependency')}</p>
        
        {/* Task Selection */}
        <div>
          <label className="text-xs text-gray-600 block mb-1">Görev Seçimi</label>
          <Select value={selectedDependency} onValueChange={setSelectedDependency}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('selectTask')} />
            </SelectTrigger>
            <SelectContent>
              {availableTasks.length === 0 ? (
                <div className="p-2 text-sm text-gray-500">{t('noAvailableTasks')}</div>
              ) : (
                availableTasks.map((task) => (
                  <SelectItem key={task.id} value={task.id}>
                    {task.title}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Dependency Type */}
        <div>
          <label className="text-xs text-gray-600 block mb-1">Bağımlılık Türü</label>
          <Select value={dependencyType} onValueChange={setDependencyType}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="FS">
                <div className="flex flex-col">
                  <span className="font-medium">FS - Finish to Start</span>
                  <span className="text-xs text-gray-500">Önceki görev bitince bu başlar</span>
                </div>
              </SelectItem>
              <SelectItem value="SS">
                <div className="flex flex-col">
                  <span className="font-medium">SS - Start to Start</span>
                  <span className="text-xs text-gray-500">Önceki görev başlayınca bu başlar</span>
                </div>
              </SelectItem>
              <SelectItem value="FF">
                <div className="flex flex-col">
                  <span className="font-medium">FF - Finish to Finish</span>
                  <span className="text-xs text-gray-500">Önceki görev bitince bu biter</span>
                </div>
              </SelectItem>
              <SelectItem value="SF">
                <div className="flex flex-col">
                  <span className="font-medium">SF - Start to Finish</span>
                  <span className="text-xs text-gray-500">Önceki görev başlayınca bu biter</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Lag Time */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-600 block mb-1">Gecikme (Gün)</label>
            <input
              type="number"
              value={lagDays}
              onChange={(e) => setLagDays(e.target.value)}
              className="w-full text-sm p-2 border rounded"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">Negatif: önce, Pozitif: sonra</p>
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1">Gecikme (Saat)</label>
            <input
              type="number"
              value={lagHours}
              onChange={(e) => setLagHours(e.target.value)}
              className="w-full text-sm p-2 border rounded"
              placeholder="0"
            />
          </div>
        </div>

        <Button
          onClick={handleAddDependency}
          disabled={!selectedDependency || adding}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-1" />
          {adding ? t('adding') : t('add')}
        </Button>
      </div>

      {/* Empty State */}
      {!dependencyChain?.depends_on?.length && !dependencyChain?.blocking?.length && (
        <div className="text-center py-6 text-gray-500 border border-dashed rounded-lg">
          <GitBranch className="h-8 w-8 mx-auto text-gray-300 mb-2" />
          <p className="text-sm">{t('noDependenciesYet')}</p>
          <p className="text-xs text-gray-400 mt-1">
            {t('noDependenciesDescription')}
          </p>
        </div>
      )}
    </div>
  );
}

export default TaskDependencies;
